/**
 * Dialog chia sẻ BĐS — Facebook, Messenger, Zalo, copy link và email.
 */
import { useCallback, useState } from 'react';
import { ActionDialog } from './ActionDialog';
import { useCloseOnScroll } from '../../shared/hooks/useCloseOnScroll';
import { TEXT } from '../../shared/languages';

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
  </svg>
);

const MessengerIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.191 14.963-3.055-3.259-5.963 3.259L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z" />
  </svg>
);

const ZaloIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="3" width="20" height="18" rx="6" fill="#0068ff" />
    <path
      d="M7 8h7l-7 8h7M16.5 16V8"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ShareDialog = ({ isOpen, onClose, title, url }) => {
  const t = TEXT.share;
  const shareUrl = (() => {
    try {
      return new URL(url || window.location.href, window.location.origin).href;
    } catch {
      return window.location.href;
    }
  })();
  const shareTitle = title || t.defaultTitle;
  const [copyMessage, setCopyMessage] = useState('');

  const handleClose = useCallback(() => {
    setCopyMessage('');
    onClose();
  }, [onClose]);

  useCloseOnScroll(isOpen, handleClose);

  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const openWindow = (targetUrl) => {
    window.open(targetUrl, '_blank', 'noopener,noreferrer,width=640,height=560');
  };

  const openShareTarget = (targetUrl) => {
    if (isMobileDevice) {
      window.location.href = targetUrl;
      return;
    }

    openWindow(targetUrl);
  };

  const shareOptions = [
    {
      id: 'facebook',
      label: t.facebook,
      icon: <FacebookIcon />,
      action: () => {
        const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`;
        openShareTarget(facebookShareUrl);
      },
    },
    {
      id: 'messenger',
      label: t.messenger,
      icon: <MessengerIcon />,
      action: () => {
        if (isMobileDevice) {
          window.location.href = `fb-messenger://share?link=${encodeURIComponent(shareUrl)}`;
          return;
        }

        openWindow(`https://www.messenger.com/t/?link=${encodeURIComponent(shareUrl)}`);
      },
    },
    {
      id: 'zalo',
      label: t.zalo,
      icon: <ZaloIcon />,
      action: async () => {
        const encodedUrl = encodeURIComponent(shareUrl);
        const shareText = encodeURIComponent(`${shareTitle}\n${shareUrl}`);
        const isAndroid = /Android/i.test(navigator.userAgent);
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (isAndroid) {
          window.location.href =
            'intent://zaloapp.com/#Intent;action=android.intent.action.SEND;type=text/plain;' +
            `S.android.intent.extra.SUBJECT=${encodeURIComponent(shareTitle)};` +
            `S.android.intent.extra.TEXT=${shareText};B.hidePostFeed=false;B.backToSource=true;end`;
          return;
        }

        if (isIOS) {
          window.location.href = `zaloshareext://shareext?url=${encodedUrl}&type=8&version=1`;
          return;
        }

        if (navigator.share) {
          try {
            await navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl });
            return;
          } catch {
            // User cancelled or share unavailable — fall through to copy.
          }
        }

        try {
          await navigator.clipboard.writeText(`${shareTitle}\n${shareUrl}`);
          setCopyMessage(t.zaloCopied);
        } catch {
          setCopyMessage(t.failed);
        }
      },
    },
    {
      id: 'copy',
      label: t.copyLink,
      icon: <LinkIcon />,
      action: async () => {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setCopyMessage(t.copied);
        } catch {
          setCopyMessage(t.failed);
        }
      },
    },
    {
      id: 'email',
      label: t.email,
      icon: <EmailIcon />,
      action: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareTitle}\n${shareUrl}`)}`;
      },
    },
  ];

  return (
    <ActionDialog
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={t.dialogTitle}
      layout="airbnb"
      panelClassName="action-dialog-panel--share-airbnb"
    >
      <h2 className="auth-dialog-title auth-dialog-title--share">{t.dialogTitle}</h2>
      <div className="share-dialog-list">
        {shareOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            className="share-dialog-item"
            onClick={option.action}
          >
            <span className="share-dialog-item-icon">{option.icon}</span>
            <span className="share-dialog-item-label">{option.label}</span>
          </button>
        ))}
      </div>
      {copyMessage && <p className="share-dialog-message">{copyMessage}</p>}
    </ActionDialog>
  );
};
